﻿/**
 * Shared format engine for all search components.
 * Handles token interpolation, pipe transforms, and markdown-to-HTML conversion.
 */

import { i18n } from '../i18n/i18n-service.js';

export function renderFormat(formatStr: string | null | undefined, doc: Record<string, any>): string {
    if (!formatStr) return '';

    const interpolated = interpolateTokens(formatStr, doc);
    return markdownToHTML(interpolated);
}

function interpolateTokens(formatStr: string, doc: Record<string, any>): string {
    return formatStr.replace(/\{([^{}]+)\}/g, (_, token: string) => {
        const parts = token.split('|');
        const fieldName = parts[0].trim();
        const pipe = parts.length > 1 ? parts.slice(1).join('|').trim() : null;
        const value = doc[fieldName];

        if (value == null) return '';
        if (pipe) return applyPipe(value, pipe);
        return String(value);
    });
}

function applyPipe(value: any, pipe: string): string {
    const [name, arg] = pipe.split(':').map((s) => s.trim());
    // Default every locale-sensitive transform to the active i18n locale so
    // dates, numbers, and currency render per-locale. Explicit arguments still
    // win, keeping existing formats backward-compatible.
    const locale = i18n.locale;
    switch (name) {
        case 'date': {
            const d = new Date(value);
            if (isNaN(d.getTime())) return String(value);
            const defaultOpts: Intl.DateTimeFormatOptions = {
                year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
            };
            let opts: Intl.DateTimeFormatOptions = defaultOpts;
            if (arg) {
                try {
                    opts = JSON.parse(arg);
                } catch (e) {
                    console.warn(`Invalid date format options: ${arg}`, e);
                }
            }
            try {
                return d.toLocaleDateString(locale, opts);
            } catch (e) {
                console.warn(`Failed to format date with options: ${JSON.stringify(opts)}`, e);
                return d.toLocaleDateString(locale, defaultOpts);
            }
        }
        case 'number': {
            const n = Number(value);
            return isNaN(n) ? String(value) : new Intl.NumberFormat(arg || locale).format(n);
        }
        case 'currency': {
            const a = Number(value);
            return isNaN(a) ? String(value) : new Intl.NumberFormat(locale, {
                style: 'currency', currency: arg || 'USD',
            }).format(a);
        }
        case 'uppercase': return String(value).toUpperCase();
        case 'lowercase': return String(value).toLowerCase();
        default: return String(value);
    }
}

function markdownToHTML(str: string): string {
    str = str.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_, alt: string, url: string) => {
        const safeUrl = sanitizeUrl(url);
        return safeUrl ? `<img alt="${alt}" src="${safeUrl}" />` : alt;
    });
    str = str.replace(/\[([^\]]*)\]\((.+)\)/g, (_, text: string, rawUrl: string) => {
        const normalizedText = text.trim();
        if (!normalizedText) return '';

        const trimmedUrl = rawUrl.trim();
        if (!trimmedUrl) return normalizedText;

        const targetBlank = /(^|\s+)_blank\s*$/i.test(trimmedUrl);
        const urlPart = targetBlank ? trimmedUrl.replace(/(^|\s+)_blank\s*$/i, '').trim() : trimmedUrl;
        if (!urlPart) return normalizedText;

        const safeUrl = sanitizeUrl(urlPart);
        if (!safeUrl) return normalizedText;

        return targetBlank
            ? `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${normalizedText}</a>`
            : `<a href="${safeUrl}">${normalizedText}</a>`;
    });
    str = str.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    str = str.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
    return str;
}

function sanitizeUrl(url: string): string | null {
    // Strip ASCII tab/newline/CR (browsers ignore these during URL parsing,
    // so attackers use them to disguise dangerous schemes, e.g. "java\tscript:").
    const trimmed = url.replace(/[\t\n\r]/g, '').trim();
    if (!trimmed) return null;

    const schemeMatch = trimmed.match(/^([a-zA-Z][a-zA-Z\d+\-.]*):/);
    if (!schemeMatch) return trimmed;

    const scheme = schemeMatch[1].toLowerCase();
    if (scheme === 'http' || scheme === 'https' || scheme === 'mailto' || scheme === 'tel') {
        return trimmed;
    }

    return null;
}
